import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { TopNavigationBar } from '@/components/ui/Navigation/TopNavigationBar';
import { LeftNavigationBar } from '@/components/ui/Navigation/LeftNavigationBar';
import { UpdateNotification } from '@/components/ui/UpdateNotification/UpdateNotification';
import { OnboardingStepCompleteBanner } from '@/components/ui/OnboardingStepCompleteBanner';
import { OnboardingGuideModal } from '@/features/Dashboard/Components/OnboardingChecklist';
import { useOnboardingProgress } from '@/contexts/OnboardingProgressContext';
import { useResponsive } from '@/contexts/ResponsiveContext';
import { PageHeaderProvider } from '@/contexts/PageHeaderContext';

import { useNavigationBlock } from '@/contexts/NavigationBlockContext';
import designTokens from '@/design-tokens';

export const AppLayout: React.FC = () => {
  const location = useLocation();
  const { isNavigationBlocked } = useNavigationBlock();
  const { isCompact } = useResponsive();
  const { isChecklistOpen, closeChecklist } = useOnboardingProgress();
  const [confirmedPath, setConfirmedPath] = useState(location.pathname);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useEffect(() => {
    // Only update confirmed path if navigation is NOT blocked
    if (!isNavigationBlocked) {
      setConfirmedPath(location.pathname);
    }
  }, [location.pathname, isNavigationBlocked]);

  // Close drawer on route change
  useEffect(() => {
    setIsDrawerOpen(false);
  }, [location.pathname]);

  // Close drawer when switching back to desktop
  useEffect(() => {
    if (!isCompact) setIsDrawerOpen(false);
  }, [isCompact]);

  const handleToggleDrawer = useCallback(() => {
    setIsDrawerOpen(prev => !prev);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  return (
    <div className="relative min-h-screen bg-[#f1f3f5]">
      {/* Fixed Navigation Bars - wrapped in error boundaries for graceful degradation */}
      <React.Suspense fallback={<div style={{height: designTokens['topnav/height'] || '3rem', backgroundColor: '#ffffff'}} />}>
        <TopNavigationBar onToggleDrawer={handleToggleDrawer} />
      </React.Suspense>
      {/* On compact viewports, sidebar is an overlay drawer; on desktop it's the normal fixed sidebar */}
      <React.Suspense fallback={!isCompact ? <div style={{width: designTokens['nav/width/collapsed'] || '4rem', position: 'fixed', left: 0, top: designTokens['topnav/height'] || '3rem', bottom: 0, backgroundColor: '#ffffff'}} /> : null}>
        <LeftNavigationBar
          currentPath={confirmedPath}
          isDrawerOpen={isDrawerOpen}
          onCloseDrawer={handleCloseDrawer}
        />
      </React.Suspense>

      {/* Main Content Area with proper spacing */}
      <div className="relative">
        {/* Spacer for fixed navigation. `app-topnav-spacer` is a PRINT hook only — nothing styles
            it on screen; see the shared `@media print` block at the end of `src/index.css`. */}
        <div
          className="app-topnav-spacer"
          style={{ height: designTokens['topnav/height'] || '3rem' }}
        />

        {/* Content container. `app-content` is likewise a print hook: the left margin here clears
            the fixed nav rail, which does not exist on paper. */}
        <div
          className="app-content transition-all duration-300"
          style={{
            marginLeft: isCompact ? 0 : (designTokens['nav/width/collapsed'] || '4rem'),
            /* Published so a print stylesheet can cancel it — the rail does not exist on paper, and
               an inline margin cannot be media-queried away from a stylesheet. Same expression as
               the margin above so the two cannot drift. */
            ['--app-content-offset' as string]: isCompact
              ? '0px'
              : (designTokens['nav/width/collapsed'] || '4rem'),
            minHeight: `calc(100dvh - ${designTokens['topnav/height'] || '3rem'})`,
            backgroundColor: '#f1f3f5',
          }}
        >
          {/* Outlet renders the matched child route */}
          <PageHeaderProvider>
            <Outlet />
          </PageHeaderProvider>
        </div>
      </div>
      <UpdateNotification />
      <OnboardingStepCompleteBanner />
      <OnboardingGuideModal isOpen={isChecklistOpen} onClose={closeChecklist} />
    </div>
  );
};